import { API_CONFIG, STORAGE_KEYS } from '../helpers/constants';

// M-4: Reference the config constant directly — removed the pointless local alias.

// W-4: STORAGE_KEYS.REFRESH_TOKEN is the codebase-wide key name for the API bearer
// credential — every other service file (agentsService, alertsService, channelDashboardService,
// etc.) uses the same key. This is intentional and consistent, not a copy-paste error.
const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    return {
        'accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    };
};

/** Payload fields are sent as query params (GET endpoints reject a body). */
const toQueryString = (payload: Record<string, unknown>): string => {
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
        }
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
};

/**
 * W-3: Throws a distinguishable error for 401/403 so callers can show
 * "not authorised" rather than a generic retry message.
 */
const assertOk = (response: Response, context: string): void => {
    if (response.status === 401 || response.status === 403) {
        throw new Error(`HTTP_${response.status}`);
    }
    if (!response.ok) {
        throw new Error(`Failed to ${context}`);
    }
};

// ── ACL Access Control ────────────────────────────────────────────────────────

export interface AclDashboardRecord {
    report_name?: string | null;
    username?: string | null;
    ticket?: string | null;
    user_type?: string | null;
    /**
     * M-1: Normalised to the string literals 'true' | 'false' | null at the
     * service boundary, regardless of whether the backend sends JSON booleans
     * or string literals — so the UI is resilient to that API-level change.
     */
    access_status?: string | null;
    manager_decision?: string | null;
    revocation_date?: string | null;
    /**
     * LIVE as of the 2026-08-10 backend deploy — confirmed against a real /acl response.
     * Replaces the earlier client-side "adm username prefix" heuristic that stood in for
     * this exact flag before the backend field existed.
     */
    is_privileged?: boolean | null;
    /** Privileged-user's AD/LDAP group. Confirmed field name is `group_name`, not `group`. */
    group_name?: string | null;
    /** Non-privileged user's functional role (e.g. "Standard User") — drives "Non-Privileged Users by Role". */
    role?: string | null;
    /** Forward-looking access-period expiry (distinct from revocation_date, a past event) — drives "Access Expiry Buckets". */
    access_end?: string | null;
    /** Approval workflow state — drives "Approval Workflow". Only observed as null/'not_set' so far. */
    approval_status?: string | null;
    /** How many reminder notifications sent for this record. Confirmed field name is `reminders_sent`, not `reminder_count`. */
    reminders_sent?: number | null;
    /** How a revocation was carried out, e.g. 'auto' | 'manual' — confirmed present in the real
     * response (currently always null in observed data) — drives the "Revoked" card's
     * auto/manual sub-breakdown. */
    revocation_method?: string | null;
    /** Server/host the finding relates to — used to scope the revoke_access / retain_access action payload. */
    instance_id?: string | null;
}

/** One quarter's snapshot from the backend's own historical tracking — the frontend has no
 * way to compute this itself (it would need every past quarter's state, not just "now"). */
export interface AclQuarterSummary {
    period: string;
    privileged_users: number;
    revocations: number;
}

/** Pre-aggregated counts from `summary.counts` in the /acl response — drives summary card subtext. */
export interface AclSummaryCounts {
    total_records?: number;
    unique_users?: number;
    privileged_users?: number;
    non_privileged_users?: number;
    expired_access?: number;
    revocation?: number;
    not_yet_revoked?: number;
    revoked?: number;
    /** Number of distinct servers scanned this run — drives "Total Records" card subtext. */
    instances?: number;
}

/** Pre-aggregated approval-stage counts from `summary.approval_workflow` — drives the
 * "Approval Workflow" chart directly, since per-record `approval_status` is null on
 * almost every observed record and can't reconstruct these stage totals client-side. */
export interface AclApprovalWorkflow {
    notified?: number;
    approved?: number;
    not_yet_revoked?: number;
    reminders?: number;
}

export interface AclRecordsResponse {
    records: AclDashboardRecord[];
    quarterOverQuarter: AclQuarterSummary[];
    summaryCounts: AclSummaryCounts;
    approvalWorkflow: AclApprovalWorkflow;
}

export interface AclFilterOptions {
    report_names: string[];
    user_types: string[];
    access_statuses: string[];
    manager_decisions: string[];
}

export type AclFilterPayload = Partial<AclDashboardRecord>;

/** M-1: Coerce JSON boolean or string boolean (any case, e.g. "True"/"False") to a canonical
 * lowercase string. String comparison is case-insensitive since the live API sends "True"/"False". */
const normaliseAccessStatus = (val: unknown): string | null => {
    if (val === true) return 'true';
    if (val === false) return 'false';
    if (typeof val === 'string') {
        const lower = val.toLowerCase();
        if (lower === 'true' || lower === 'false') return lower;
        return val;
    }
    return val != null ? String(val) : null;
};

/** W-5: Accepts an AbortSignal to cancel in-flight requests on remount. */
export const getAclFilters = async (signal?: AbortSignal): Promise<AclFilterOptions> => {
    const response = await fetch(`${API_CONFIG.SHAY_DASHBOARD_BASE_URL}/acl/filters`, {
        method: 'GET',
        headers: getAuthHeaders(),
        signal,
    });
    assertOk(response, 'fetch ACL filter options');
    const data = await response.json();
    return {
        report_names: data?.report_names ?? [],
        user_types: data?.user_types ?? [],
        access_statuses: data?.access_statuses ?? [],
        manager_decisions: data?.manager_decisions ?? [],
    };
};

/**
 * B-2: channelId is forwarded as a query param so the backend can scope
 * results to the caller's authorised channel/tenant server-side.
 * W-5: Accepts an AbortSignal to cancel in-flight requests on remount.
 */
export const getAclRecords = async (
    payload: AclFilterPayload = {},
    channelId?: string,
    signal?: AbortSignal,
): Promise<AclRecordsResponse> => {
    const params: Record<string, unknown> = {
        ...payload,
        ...(channelId ? { channel_id: channelId } : {}),
    };
    const response = await fetch(
        `${API_CONFIG.SHAY_DASHBOARD_BASE_URL}/acl${toQueryString(params)}`,
        { method: 'GET', headers: getAuthHeaders(), signal },
    );
    assertOk(response, 'fetch ACL records');
    const data = await response.json();
    const rawRecords: AclDashboardRecord[] = Array.isArray(data)
        ? data
        : (data?.records ?? data?.results ?? data?.data ?? []);
    // M-1: normalise access_status at the service boundary.
    const records = rawRecords.map(r => ({ ...r, access_status: normaliseAccessStatus(r.access_status) }));

    const summaryCounts: AclSummaryCounts = Array.isArray(data) ? {} : (data?.summary?.counts ?? {});
    const approvalWorkflow: AclApprovalWorkflow = Array.isArray(data) ? {} : (data?.summary?.approval_workflow ?? {});

    // "Privileged Users — Quarter over Quarter" needs true historical snapshots, which no
    // per-record field can provide — the backend sends this as a separate pre-aggregated
    // array in the same response, alongside the filterable record list.
    const rawQuarterOverQuarter: AclQuarterSummary[] = Array.isArray(data)
        ? []
        : (data?.summary?.quarter_over_quarter ?? []);
    // Per-quarter `revocations` isn't populated by the backend's historical snapshot yet
    // (always 0) — use the live aggregate `summary.counts.revocation` instead, per card update.
    const quarterOverQuarter = rawQuarterOverQuarter.map(q => ({
        ...q,
        revocations: summaryCounts.revocation ?? q.revocations,
    }));

    return { records, quarterOverQuarter, summaryCounts, approvalWorkflow };
};

// ── Trusted Advisor ───────────────────────────────────────────────────────────

export interface TaDashboardRecord {
    id?: number | null;
    report_name?: string | null;
    category?: string | null;
    summary?: string | null;
    remediation?: string | null;
    status?: string | null;
    /**
     * LIVE as of the 2026-08-11 backend deploy — confirmed against a real /ta response.
     * Field names below match the real contract, which differs from our earlier guesses
     * (owner → owner_team, ticket → ticket_id, timestamp → first_seen_at).
     */
    /** AWS account the finding belongs to — drives "Findings by Account". */
    account?: string | null;
    /** Team responsible for remediation (e.g. "Server Team") — drives "Findings by Owner/Team". Confirmed field name is `owner_team`, not `owner`. */
    owner_team?: string | null;
    /** Individual assigned within the owning team. Not yet consumed by any panel. */
    assigned_member?: string | null;
    /** Linked change-request ticket ID. Confirmed field name is `ticket_id`, not `ticket`. */
    ticket_id?: string | null;
    /** Ticket lifecycle state, e.g. 'open' — paired with `ticket_id` for "Change Request Status" buckets. */
    ticket_status?: string | null;
    ticket_created_at?: string | null;
    closed_at?: string | null;
    /** Not yet consumed by any panel — TA has no "Approval Workflow" panel today (unlike ACL). */
    approval_status?: string | null;
    approved_at?: string | null;
    /** e.g. 'auto' | 'manual' — not yet consumed by any panel. */
    remediation_status?: string | null;
    /** ServiceNow assignment group sys_id — used when closing a change request. */
    assignment_group?: string | null;
    /** Short finding title (e.g. "Amazon S3 Bucket Permissions") — used as `recommendations` in the apply_remediation action payload. */
    recommendations?: string | null;
    /** AWS resource ARN the finding applies to — used in the apply_remediation action payload. */
    resource?: string | null;
    /** e.g. 'AWS' | 'Customer' — used in the apply_remediation action payload. */
    responsibility?: string | null;
    /** Recommended action, e.g. 'Exclude' — used in the apply_remediation action payload. */
    action?: string | null;
    fetched_at?: string | null;
    /** When this finding was first observed (ISO date) — drives "Open-Finding Aging" and
     * "Findings Over Time". Confirmed field name is `first_seen_at`, not `timestamp`. */
    first_seen_at?: string | null;
}

export interface TaFilterOptions {
    report_names: string[];
    categories: string[];
    statuses: string[];
}

export type TaFilterPayload = Partial<TaDashboardRecord>;

/** Pre-aggregated counts from `summary.counts` in the /ta response — drives summary card subtext
 * (percentages, awaiting-approval counts, ticket breakdowns) that can't be recomputed
 * client-side identically to how the backend derives them. */
export interface TaSummaryCounts {
    action_required?: number;
    investigations?: number;
    investigations_awaiting_approval?: number;
    open_change_requests?: number;
    closed_change_requests?: number;
    no_ticket?: number;
    action_percentage?: number;
    investigation_percentage?: number;
}

export interface TaRecordsResponse {
    records: TaDashboardRecord[];
    summaryCounts: TaSummaryCounts;
}

/** W-5: Accepts an AbortSignal. */
export const getTaFilters = async (signal?: AbortSignal): Promise<TaFilterOptions> => {
    const response = await fetch(`${API_CONFIG.SHAY_DASHBOARD_BASE_URL}/ta/filters`, {
        method: 'GET',
        headers: getAuthHeaders(),
        signal,
    });
    assertOk(response, 'fetch Trusted Advisor filter options');
    const data = await response.json();
    return {
        report_names: data?.report_names ?? [],
        categories: data?.categories ?? [],
        statuses: data?.statuses ?? [],
    };
};

/**
 * B-2: channelId forwarded as a query param for server-side scoping.
 * W-5: Accepts an AbortSignal.
 */
export const getTaRecords = async (
    payload: TaFilterPayload = {},
    channelId?: string,
    signal?: AbortSignal,
): Promise<TaRecordsResponse> => {
    const params: Record<string, unknown> = {
        ...payload,
        ...(channelId ? { channel_id: channelId } : {}),
    };
    const response = await fetch(
        `${API_CONFIG.SHAY_DASHBOARD_BASE_URL}/ta${toQueryString(params)}`,
        { method: 'GET', headers: getAuthHeaders(), signal },
    );
    assertOk(response, 'fetch Trusted Advisor records');
    const data = await response.json();
    const records: TaDashboardRecord[] = Array.isArray(data)
        ? data
        : (data?.records ?? data?.results ?? data?.data ?? []);
    const summaryCounts: TaSummaryCounts = Array.isArray(data) ? {} : (data?.summary?.counts ?? {});
    return { records, summaryCounts };
};

export interface CloseChangeRequestPayload {
    channel_id: string;
    change_request_id: string;
    comment: string;
    assignment_group: string;
    close_code: string;
    force_approve: boolean;
}

export interface CloseChangeRequestResponse {
    success: boolean;
    change_number?: string | null;
    sys_id?: string | null;
    state?: string | null;
    link?: string | null;
    blocked_needs_approval?: unknown;
    blocked_transition?: unknown;
    pending_approvals?: unknown;
    error?: string | null;
    status_code?: string | null;
}

export const closeChangeRequest = async (
    payload: CloseChangeRequestPayload,
): Promise<CloseChangeRequestResponse> => {
    const response = await fetch(
        `${API_CONFIG.ML_API_BASE_URL}/shay/close_change_request`,
        {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        },
    );
    assertOk(response, 'close change request');
    return response.json();
};

// ── ACL / TA actions (revoke access, retain access, apply remediation) ────────

export type AclActionRequestType = 'revoke_access' | 'apply_remediation' | 'retain_access';

export interface AclActionRequest {
    request_type: AclActionRequestType;
    report_name: string;
    username?: string;
    instance_id?: string;
    category?: string;
    status?: string;
    recommendations?: string;
    remediation?: string;
    resource?: string;
    responsibility?: string;
    action?: string;
}

export interface AclActionResponse {
    status: string;
    request_type: AclActionRequestType;
    execution_arn?: string | null;
    rows_updated?: number | null;
    message?: string | null;
}

export const postAclAction = async (
    payload: AclActionRequest,
): Promise<AclActionResponse> => {
    const response = await fetch(
        `${API_CONFIG.SHAY_DASHBOARD_BASE_URL}/approvals`,
        {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        },
    );
    assertOk(response, 'submit action');
    return response.json();
};
